<template>
  <div class="w-full h-full bg-n-background overflow-auto">
    <!-- Header -->
    <div class="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
      <div class="px-6 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
              Productos CRM
            </h2>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Administra el catálogo de productos para los deals
            </p>
          </div>
          <Button
            icon="i-lucide-circle-plus"
            label="Nuevo Producto"
            @click="openCreateModal"
          />
        </div>
      </div>
      
      <!-- Filters -->
      <div class="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Search -->
          <div class="flex-1 min-w-64">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Buscar por nombre, SKU o descripción"
              class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg 
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                     placeholder-slate-500 dark:placeholder-slate-400
                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
          </div>
          
          <!-- Category Filter -->
          <div class="relative">
            <select
              v-model="categoryFilter"
    class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg
      bg-white dark:bg-slate-700 text-slate-900 dark:text-white
      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
      appearance-none pr-6"
            >
              <option value="">Todas las categorías</option>
              <option v-for="category in uniqueCategories" :key="category" :value="category">
                {{ category }}
              </option>
            </select>
          </div>
          
          <!-- Status Filter -->
          <div class="flex bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
            <button
              @click="activeFilter = ''"
              :class="[
                'px-3 py-1 text-sm font-medium rounded transition-colors',
                activeFilter === ''
                  ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              ]"
            >
              Todos
            </button>
            <button
              @click="activeFilter = 'active'"
              :class="[
                'px-3 py-1 text-sm font-medium rounded transition-colors',
                activeFilter === 'active'
                  ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              ]"
            >
              Activos
            </button>
            <button
              @click="activeFilter = 'inactive'"
              :class="[
                'px-3 py-1 text-sm font-medium rounded transition-colors',
                activeFilter === 'inactive'
                  ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              ]"
            >
              Inactivos
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Content -->
    <div class="p-6">
      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center h-64">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="products.length === 0" class="flex flex-col items-center justify-center h-64">
        <div class="text-slate-400 text-6xl mb-4">📦</div>
        <h3 class="text-lg font-medium text-slate-900 dark:text-white mb-2">
          No hay productos
        </h3>
        <p class="text-slate-600 dark:text-slate-400 text-center max-w-md mb-4">
          Comienza creando tu primer producto para usar en los deals.
        </p>
        <Button
          icon="i-lucide-circle-plus"
          label="Crear Primer Producto"
          @click="openCreateModal"
        />
      </div>
      
      <!-- No Results State -->
      <div v-else-if="filteredProducts.length === 0" class="flex flex-col items-center justify-center h-64">
        <div class="text-slate-400 text-6xl mb-4">🔍</div>
        <h3 class="text-lg font-medium text-slate-900 dark:text-white mb-2">
          No se encontraron productos
        </h3>
        <p class="text-slate-600 dark:text-slate-400 text-center max-w-md">
          Intenta ajustar los filtros de búsqueda.
        </p>
      </div>
      
      <!-- Products Table -->
      <div v-else class="w-full overflow-hidden border border-slate-200 dark:border-slate-700 rounded-lg">
        <table class="w-full">
          <thead class="bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Producto
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                SKU
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Precio
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Categoría
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Deals
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Estado
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
            <tr
              v-for="product in filteredProducts"
              :key="product.id"
              class="hover:bg-slate-50 dark:hover:bg-slate-750"
            >
              <td class="px-6 py-4">
                <div>
                  <div class="text-sm font-medium text-slate-900 dark:text-white">
                    {{ product.name }}
                  </div>
                  <div v-if="product.description" class="text-sm text-slate-500 dark:text-slate-400 truncate max-w-xs">
                    {{ product.description }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-white">
                {{ product.sku || '-' }}
              </td>
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-white">
                {{ product.formatted_price }}
              </td>
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-white">
                <span v-if="product.category" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ring-slate-300 text-slate-700 dark:text-slate-200 dark:ring-slate-600">
                  {{ product.category }}
                </span>
                <span v-else>-</span>
              </td>
              <td class="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                {{ product.deals_count }}
              </td>
              <td class="px-6 py-4">
                <button
                  @click="toggleProductStatus(product)"
                  :class="[
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors',
                    product.active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/60'
                      : 'bg-slate-100 text-slate-800 dark:bg-slate-600 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-500'
                  ]"
                >
                  {{ product.active ? 'Activo' : 'Inactivo' }}
                </button>
              </td>
              <td class="px-6 py-4 text-right text-sm">
                <div class="flex items-center justify-end space-x-2">
                  <button
                    @click="openEditModal(product)"
                    class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    title="Editar producto"
                  >
                    <i class="i-lucide-edit w-4 h-4"></i>
                  </button>
                  <button
                    @click="openDeleteModal(product)"
                    class="text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Eliminar producto"
                  >
                    <i class="i-lucide-trash-2 w-4 h-4"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- Create/Edit Product Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <div class="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
            {{ isEditing ? 'Editar Producto' : 'Nuevo Producto' }}
          </h3>

          <!-- Botón X -->
          <button
            @click="closeModal"
            class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Nombre <span class="text-red-600">*</span>
            </label>
            <input
              v-model="productForm.name"
              type="text"
              required
              class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Nombre del producto"
            >
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Descripción
            </label>
            <textarea
              v-model="productForm.description"
              rows="3"
              class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descripción del producto"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              SKU <span class="text-red-600">*</span>
            </label>
            <input
              v-model="productForm.sku"
              type="text"
              :class="['w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500', duplicateSku ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '']"
              placeholder="Código SKU"
            >
            <p v-if="duplicateSku" class="mt-1 text-xs text-red-600">
              Este SKU ya existe. Ingresá otro.
            </p>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Precio
              </label>
              <input
                v-model.number="productForm.price_cents"
                type="number"
                required
                min="0"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg
                       bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Moneda
              </label>
              <div class="relative">
                <select
                  v-model="productForm.currency"
                  class="w-full px-3 pr-8 py-2 border border-slate-200 dark:border-slate-600 rounded-lg
                        bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        appearance-none"
                >
                  <option value="USD">USD</option>

                  <option value="ARS">ARS</option>
                </select>

                <!-- Ícono de flecha -->
                <span class="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <svg class="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Categoría
            </label>
            <input
              v-model="productForm.category"
              type="text"
              class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Categoría del producto"
            >
          </div>
          
          <div class="flex items-center">
            <input
              v-model="productForm.active"
              type="checkbox"
              id="product-active"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
            >
            <label for="product-active" class="ml-2 text-sm text-slate-700 dark:text-slate-300">
              Producto activo
            </label>
          </div>
        </div>
        
        <div class="px-6 py-4 flex justify-end space-x-3">
          <Button
            variant="hollow"
            label="Cancelar"
            class="px-6 py-3 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all font-medium"
            @click="closeModal"
          />
          <Button
            :label="isEditing ? 'Actualizar' : 'Crear'"
            :disabled="isSubmitting || !productForm.name || duplicateSku"
            @click="saveProduct"
          />
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click.self="closeDeleteModal"
    >
      <div class="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full">
        <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
            Confirmar Eliminación
          </h3>
        </div>
        
        <div class="p-6">
          <p class="text-slate-600 dark:text-slate-400">
            ¿Estás seguro de que quieres eliminar el producto 
            <span class="font-semibold text-slate-900 dark:text-white">{{ selectedProduct?.name }}</span>?
            Esta acción no se puede deshacer.
          </p>
        </div>
        
        <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
          <Button
            variant="hollow"
            label="Cancelar"
            class="px-6 py-3 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all font-medium"
            @click="closeDeleteModal"
          />
          <Button
            variant="alert"
            label="Eliminar"
            class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg cursor-pointer border-0 outline-none transition-all font-medium flex items-center"
            @click="confirmDelete"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CRMProductsAPI from 'dashboard/api/crm/products';
import Button from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'CrmProductsIndex',
  components: {
    Button,
  },
  data() {
    return {
      products: [],
      loading: false,
      searchQuery: '',
      categoryFilter: '',
      activeFilter: '',
      showModal: false,
      showDeleteModal: false,
      isSubmitting: false,
      isEditing: false,
      selectedProduct: null,
      productForm: {
        name: '',
        description: '',
        sku: '',
        price_cents: 0,
        currency: 'USD',
        category: '',
        active: true,
      },
    };
  },
  computed: {
    duplicateSku() {
      const sku = (this.productForm.sku || '').trim();
      if (!sku) return false;
      return this.products.some(p => p.sku === sku && (!this.selectedProduct || p.id !== this.selectedProduct.id));
    },
    filteredProducts() {
      return this.products.filter(product => {
        const matchesSearch = !this.searchQuery || 
          product.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (product.sku && product.sku.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
          (product.description && product.description.toLowerCase().includes(this.searchQuery.toLowerCase()));
        
        const matchesCategory = !this.categoryFilter || product.category === this.categoryFilter;
        
        const matchesActive = !this.activeFilter || 
          (this.activeFilter === 'active' && product.active) ||
          (this.activeFilter === 'inactive' && !product.active);
        
        return matchesSearch && matchesCategory && matchesActive;
      });
    },
    uniqueCategories() {
      return [...new Set(this.products.map(p => p.category).filter(Boolean))];
    },
  },
  async mounted() {
    await this.fetchProducts();
  },
  
methods: {
  async fetchProducts() {
    try {
      this.loading = true;
      const response = await CRMProductsAPI.get();
      this.products = response.data || [];
    } catch (error) {
      console.error('Error fetching products:', error);
      this.$toast?.error ? this.$toast.error('Error al cargar los productos') : alert('Error al cargar los productos');
    } finally {
      this.loading = false;
    }
  },

  openCreateModal() {
    this.isEditing = false;
    this.productForm = {
      name: '',
      description: '',
      sku: '',
      price_cents: 0,
      currency: 'USD',
      category: '',
      active: true,
    };
    this.showModal = true;
  },

  openEditModal(product) {
    this.isEditing = true;
    this.selectedProduct = product;
    this.productForm = {
      name: product.name || '',
      description: product.description || '',
      sku: product.sku || '',
      price_cents: typeof product.price_cents === 'number' ? Math.round(product.price_cents / 100) : 0,
      currency: product.currency || 'USD',
      category: product.category || '',
      active: !!product.active,
    };
    this.showModal = true;
  },

  closeModal() {
    this.showModal = false;
    this.selectedProduct = null;
  },

  async saveProduct() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      // Transform price to cents for API
      const payload = { ...this.productForm };
      const units = Number(payload.price_cents || 0);
      payload.price_cents = Number.isFinite(units) ? Math.round(units * 100) : 0;
      if (this.isEditing && this.selectedProduct) {
        await CRMProductsAPI.update(this.selectedProduct.id, payload);
        this.$toast?.success && this.$toast.success('Producto actualizado correctamente');
      } else {
        await CRMProductsAPI.create(payload);
        this.$toast?.success && this.$toast.success('Producto creado correctamente');
      }
      await this.fetchProducts();
      this.closeModal();
      this.resetForm();
    } catch (error) {
      console.error('Error guardando producto:', error);
      const msg = error?.response?.data?.message || 'Error al guardar el producto';
      this.$toast?.error ? this.$toast.error(msg) : alert(msg);
    } finally {
      this.isSubmitting = false;
    }
  },

  resetForm() {
    this.productForm = {
      name: '',
      description: '',
      sku: '',
      price_cents: 0,
      currency: 'USD',
      category: '',
      active: true,
    };
  },

  openDeleteModal(product) {
    this.selectedProduct = product;
    this.showDeleteModal = true;
  },

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.selectedProduct = null;
  },

  async confirmDelete() {
    try {
      await CRMProductsAPI.delete(this.selectedProduct.id);
      this.$toast?.success && this.$toast.success('Producto eliminado correctamente');
      this.closeDeleteModal();
      await this.fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      this.$toast?.error ? this.$toast.error('Error al eliminar el producto') : alert('Error al eliminar el producto');
    }
  },

    async toggleProductStatus(product) {
    try {
      const newStatus = !product.active;
      await CRMProductsAPI.update(product.id, { active: newStatus });
      product.active = newStatus; // UI instantánea
      this.$toast?.success && this.$toast.success(`Producto ${newStatus ? 'activado' : 'desactivado'} correctamente`);
    } catch (error) {
      console.error('Error toggling product status:', error);
      this.$toast?.error ? this.$toast.error('No se pudo cambiar el estado') : alert('No se pudo cambiar el estado');
    }
  },
  },
};
</script>
